import React, { useState, useRef, useEffect } from "react";
import { checkAllPainQuestionAnswered, getFirstNotAnsweredQuestionInd } from "../Services/MovementService";
import AlertModal from "../../AlertModal";
import PerfectScrollbar from "react-perfect-scrollbar";
import scrollIntoView from "scroll-into-view-if-needed";
import smoothScrollIntoView from "smooth-scroll-into-view-if-needed";
import crossBtn from "../../../../assets/img/utils/cross.svg";
import CONSTANT from "../../../../constants/Constant";
import PainQuestion from "./PainQuestion";
import {
    getConditionalQuestionIds,
    willShowQuestion
} from "../utilities/PainQuestionFormUtils";
import { useAzureSpeech } from "../utilities/useAzureSpeech";
import { getMessageByKeyTexts, playAudio, playAudioWithRetry } from "../../../../utils/Utils";
import GLOBAL from "../../../../global";

const PainQuestionForm = ({ painQuestionList, setPainQuestionList, isClickedOnSubmitBtn, onClickAnalyzeHandler, isAnalyzingVideo, realTimeDetectionConstrains }) => {
    const [nextNotAnsweredPainQuestionInd, setNextNotAnsweredPainQuestionInd] = useState(-1);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showListeningText, setShowListeningText] = useState(false);
    const [editManuallyAudio, setEditManuallyAudio] = useState(false);

    const painQuestionRef = useRef(null);
    const conditionalQuestionListSetRef = useRef(new Set());
    const userDismissedDialogRef = useRef(false);

    const { startListening, stopListening, isListening, transcript, lastError, countdown } = useAzureSpeech();


    // get waiting time
    const voiceTimeFeat = GLOBAL.TENANT_FEATURES.find(
        item => item.Name === "VoiceWaitingTimeInSeconds"
    );
    const {
        Value: waitingTime = 35,
    } = voiceTimeFeat ?? {};

    // get next question waiting time
    const nextQuesTimeFeat = GLOBAL.TENANT_FEATURES.find(
        item => item.Name === "MultipleVoiceWaitingTimeInSeconds"
    );
    const {
        Value: nextQuesTime = 6,
    } = nextQuesTimeFeat ?? {};



    // get is voice enabled
    const voiceEnabledFeat = GLOBAL.TENANT_FEATURES.find(
        item => item.Name === "IsBiometricVoiceInteractionEnable"
    );

    const isVoiceEnabled = voiceEnabledFeat?.Value === "True";


    console.log("voiceenabled, waiting time", { isVoiceEnabled, waitingTime });

    // console.log("just chcecking------pain question form-------",getMessageByKeyTexts(realTimeDetectionConstrains, "BiometricVoiceInteraction", "dowantsave").audio)

    // Listen to transcript for auto confirm/cancel
    useEffect(() => {
        if (!transcript || !isVoiceEnabled) return;

        const handleTranscript = async () => {
            const lower = transcript.toLowerCase().trim();

            if (lower.includes("yes")) {
                onClickYesAtConfirmDialog();
                stopListening();
            } else if (lower.includes("no")) {
                onClickNoConfirmDialog();
                stopListening();
            }
        };

        handleTranscript();
    }, [transcript]);


    // Auto start/stop speech when dialog is open
    useEffect(() => {
        let isCancelled = false;

        const handleAudioAndListening = async () => {
            if (showConfirmDialog) {
                try {
                    // Stop any ongoing recognizer before playing audio
                    stopListening();
                    setShowListeningText(false);

                    // Play "remove" sound effect (if needed)
                    await playAudioWithRetry("remove");

                    // Get confirmation audio from constraints
                    const { audio } = getMessageByKeyTexts(
                        realTimeDetectionConstrains,
                        "BiometricVoiceInteraction",
                        "dowantsave"
                    ) || {};

                    let listeningPromise;
                    if (audio && isVoiceEnabled && !isNoHasBeenClicked) {
                        // Start listening immediately before playing audio
                        listeningPromise = new Promise(resolve => {
                            startListening({ language: "en-US", timeoutSeconds: waitingTime });
                            setTimeout(resolve, waitingTime * 1000);
                        });

                        // Play audio
                        await playAudioWithRetry(audio, { priorityLevel: 2 });

                        // Show listening text after audio finishes
                        if (!isCancelled) {
                            setShowListeningText(true);
                        }
                    } else if (isVoiceEnabled) {
                        // If no audio, start listening immediately and show text
                        if (!isCancelled) {
                            setShowListeningText(true);
                            listeningPromise = new Promise(resolve => {
                                startListening({ language: "en-US", timeoutSeconds: waitingTime });
                                setTimeout(resolve, waitingTime * 1000);
                            });
                        }
                    }

                    if (listeningPromise) {
                        // Wait for listening to complete
                        await listeningPromise;

                        if (!isCancelled && !userDismissedDialogRef.current && !hasConfirmedRef.current) {
                            const { audio: editAudio } = getMessageByKeyTexts(
                                realTimeDetectionConstrains,
                                "BiometricVoiceInteraction",
                                "editresmanually"
                            ) || {};
                            if (editAudio && isVoiceEnabled) {
                                await playAudioWithRetry(editAudio, { priorityLevel: 2 });
                                setEditManuallyAudio(true);
                                setIsNoHasBeenClicked(true);
                            }
                        }
                    }
                } catch (err) {
                    console.error("Audio playback or listening error:", err);
                }
            } else {
                // Stop listening when dialog closes
                stopListening();
                setShowListeningText(false);
            }
        };

        handleAudioAndListening();

        // Cleanup in case the dialog closes mid-audio
        return () => {
            isCancelled = true;
            stopListening();
            setShowListeningText(false);
        };
    }, [showConfirmDialog]);


    useEffect(() => {
        conditionalQuestionListSetRef.current = getConditionalQuestionIds(painQuestionList);

        painQuestionList.length > 0
            ? updateNextNotAnsweredQuestionInxState(painQuestionList)
            : setNextNotAnsweredPainQuestionInd(-1);

    }, [painQuestionList]);

    const updateNextNotAnsweredQuestionInxState = (painQuestionList, moveNextQuestionAsClickedNextOnCheckBoxQuestion = false) => {
        let newNextNotAnsweredPainQuestionInd = getFirstNotAnsweredQuestionInd(painQuestionList, [...conditionalQuestionListSetRef.current]);
        if (painQuestionList[nextNotAnsweredPainQuestionInd]?.QuestionType === CONSTANT.ELEMENT_TYPES.CHECKBOX && !moveNextQuestionAsClickedNextOnCheckBoxQuestion) {
            return;
        }

        setNextNotAnsweredPainQuestionInd(newNextNotAnsweredPainQuestionInd);

        // Check if all questions are answered after updating the state
        // Only show dialog if user hasn't explicitly dismissed it
        if (newNextNotAnsweredPainQuestionInd === -1 && checkAllPainQuestionAnswered(painQuestionList) && !userDismissedDialogRef.current) {
            setShowConfirmDialog(true);
        }
    }

    const autoTriggerTimerRef = useRef(null);

    const onChangeAnswer = (question, answerId) => {
        // Reset the dismissed flag when user starts changing answers
        userDismissedDialogRef.current = false;

        let pQuestionList = painQuestionList;
        let pQuestion = pQuestionList.find(x => x.QuestionId === question.QuestionId);

        if (pQuestion) {
            if (pQuestion?.QuestionType === CONSTANT.ELEMENT_TYPES.CHECKBOX) {
                let tempAnswerList = pQuestion?.AnswerList ?? [];
                let doesAlreadySelectedCurrentAnswer = tempAnswerList.find(answer => answer === answerId);
                if (doesAlreadySelectedCurrentAnswer) {
                    tempAnswerList = tempAnswerList.filter(currentAnswerId => currentAnswerId !== answerId);
                } else {
                    tempAnswerList.push(answerId);
                }

                pQuestion.AnswerList = tempAnswerList;
                pQuestion.AnswerId = 0;
            } else {
                pQuestion.AnswerId = answerId;
                pQuestion.AnswerList = [];
            }
        }
        setPainQuestionList(prev => pQuestionList.slice());
        if (checkAllPainQuestionAnswered(pQuestionList.slice()) && question.QuestionType !== CONSTANT.ELEMENT_TYPES.CHECKBOX && !userDismissedDialogRef.current) {
            setShowConfirmDialog(true);
        }
        question.QuestionType !== CONSTANT.ELEMENT_TYPES.CHECKBOX && onExecuteScroll(question.QuestionId);
        updateNextNotAnsweredQuestionInxState(pQuestionList);
        clearAnswersForIrrelevantQuestions();

        /// Reset and restart the 6-second timer
        if (autoTriggerTimerRef.current) {
            clearTimeout(autoTriggerTimerRef.current);
        }

        autoTriggerTimerRef.current = setTimeout(() => {
            updateNextNotAnsweredQuestionInxState(
                pQuestionList,
                question.QuestionType === CONSTANT.ELEMENT_TYPES.CHECKBOX
            );
        }, nextQuesTime * 1000);
    };

    const clearAnswersForIrrelevantQuestions = () => {
        const allChildQuestionsId = [...conditionalQuestionListSetRef.current];
        const updatedPainQuestions = painQuestionList.map(question => {
            if (allChildQuestionsId.includes(question.QuestionId) && !willShowQuestion(question, painQuestionList, [...conditionalQuestionListSetRef.current])) {
                if (question.QuestionType === CONSTANT.ELEMENT_TYPES.CHECKBOX) {
                    return { ...question, AnswerList: [], AnswerId: 0 };
                } else {
                    return { ...question, AnswerList: [], AnswerId: 0 };
                }
            }
            return question;
        });

        setPainQuestionList(updatedPainQuestions);
    };

    const [isNoHasBeenClicked, setIsNoHasBeenClicked] = useState(false);
    const noConfirmProcessingRef = useRef(false);


    const onClickNoConfirmDialog = async () => {
        if (showConfirmDialog && !noConfirmProcessingRef.current) {
            noConfirmProcessingRef.current = true;
            stopListening();
            // Mark that user dismissed the dialog
            userDismissedDialogRef.current = true;

            // Play "editresmanually" audio when No is selected
            const { audio } = getMessageByKeyTexts(
                realTimeDetectionConstrains,
                "BiometricVoiceInteraction",
                "editresmanually"
            ) || {};

            if (audio && isVoiceEnabled && !isNoHasBeenClicked && !editManuallyAudio) {
                await playAudioWithRetry(audio, { priorityLevel: 2 });
                setIsNoHasBeenClicked(true);
            }

            closeConfirmDialog();
            noConfirmProcessingRef.current = false;
        }
    }

    const closeConfirmDialog = () => {
        setShowConfirmDialog(false);
    }

    const hasConfirmedRef = useRef(false);

    const onClickYesAtConfirmDialog = async () => {
        if (hasConfirmedRef.current) return; // already confirmed
        hasConfirmedRef.current = true;

        try {
            onClickAnalyzeHandler && await onClickAnalyzeHandler();
        } finally {
            closeConfirmDialog();
            hasConfirmedRef.current = false; // reset for next time
        }
    };


    const getIndexOfQuestionId = (questionId) => {
        return painQuestionList.findIndex(item => item.QuestionId === questionId)
    }

    const getNextQuestionIndex = (questionId) => {
        const currentQuestionInd = getIndexOfQuestionId(questionId);
        const painQuestionsLength = painQuestionList.length;
        if (currentQuestionInd === painQuestionsLength - 1) return -1;
        else return currentQuestionInd + 1;
    }

    const getBlurStyle = () => {
        return (nextNotAnsweredPainQuestionInd > -1) ? " pointer-none blurry z-0" : "";
    }

    if (!painQuestionList || painQuestionList.length === 0)
        return;

    const onExecuteScroll = (questionId) => {
        try {
            const scrollIntoViewSmoothly =
                'scrollBehavior' in document.documentElement.style
                    ? scrollIntoView
                    : smoothScrollIntoView

            const nextPainQuestionIndex = getNextQuestionIndex(questionId);
            if (nextPainQuestionIndex === -1) return;
            const nextPainQuestionId = painQuestionList[nextPainQuestionIndex].QuestionId;

            setTimeout(() => {
                const element = document.getElementById(`pain-question-${nextPainQuestionId}`);

                if (!questionId && painQuestionRef.current) {
                    painQuestionRef.current.scrollTop = 0;
                    return;
                }

                if (element) {
                    // noinspection JSIgnoredPromiseFromCall
                    scrollIntoViewSmoothly(element);
                }
            }, 500);
        } catch (error) {
            console.error(error);
        }
    };


    return (
        <div className="position-relative">
            {nextNotAnsweredPainQuestionInd > -1 && (
                <div className="position-sticky top-0 w-100 z-10 bg-color-white pain-question__not-answered">
                    <div className="pain-question__container d-flex justify-content-center mmh-align-items-end flex-column">
                        <div>
                            {painQuestionList[nextNotAnsweredPainQuestionInd] && (
                                <PainQuestion
                                    key={nextNotAnsweredPainQuestionInd}
                                    painQuestion={painQuestionList[nextNotAnsweredPainQuestionInd]}
                                    onChangeAnswer={onChangeAnswer}
                                    isClickedOnSubmitBtn={isClickedOnSubmitBtn}
                                    isSpeechEnabled={isVoiceEnabled}
                                    waitingTime={waitingTime}
                                    realTimeDetectionConstrains={realTimeDetectionConstrains}
                                />
                            )}
                        </div>

                        {painQuestionList[nextNotAnsweredPainQuestionInd]?.QuestionType === CONSTANT.ELEMENT_TYPES.CHECKBOX && (
                            <button
                                type="button"
                                className="btn bg-color-bright-light-green text-white ml-auto margin-bottom-2-rem"
                                onClick={() => {
                                    if (isVoiceEnabled) {
                                        playAudioWithRetry("remove");
                                    }
                                    updateNextNotAnsweredQuestionInxState(painQuestionList, true)
                                }}>
                                Next
                            </button>
                        )}
                    </div>
                    <div className="mr-3 mb-3 cursor-pointer pain-question__cross_btn" onClick={() => {
                        setNextNotAnsweredPainQuestionInd(-1)
                        playAudio("remove")
                    }}>
                        <img src={crossBtn} alt="cross" className="w-100" />
                    </div>
                </div>
            )}

            <PerfectScrollbar
                containerRef={el => (painQuestionRef.current = el)}
                className={`${getBlurStyle()}`}>
                <div className="pain-question-modal">
                    {painQuestionList.map((painQuestion, index) => (
                        <div className="">
                            {willShowQuestion(painQuestion, painQuestionList, [...conditionalQuestionListSetRef.current]) ? (
                                <PainQuestion
                                    key={index}
                                    painQuestion={painQuestion}
                                    onChangeAnswer={onChangeAnswer}
                                    isClickedOnSubmitBtn={isClickedOnSubmitBtn}
                                    isSpeechEnabled={false}
                                    waitingTime={waitingTime}
                                    realTimeDetectionConstrains={realTimeDetectionConstrains}

                                />
                            )
                                : ""
                            }
                        </div>
                    ))}

                    <AlertModal
                        show={showConfirmDialog && !isNoHasBeenClicked}
                        confirmBtnText="Yes"
                        cancelBtnText="No"
                        showConfirm={true}
                        showCancel={true}
                        showCloseButton={true}
                        onConfirm={onClickYesAtConfirmDialog}
                        onCancel={onClickNoConfirmDialog}
                        onClose={onClickNoConfirmDialog}
                        showLoadingInConform={isAnalyzingVideo}
                        closeOnClickOutside={true}
                        disabledText="Saving..."
                    >
                        <>
                            <div className={"font-size-1_3rem padding-bottom-1-rem"}>
                                Confirmation
                            </div>
                            <br />
                            Would you like to save your result?
                            {isVoiceEnabled && isListening && showListeningText && (
                                <div className="text-center text-muted" style={{ marginTop: '10px', color: "#0075B6" }}>
                                    <span className="font-size-1_3rem text-color-blue">🔊 Listening..</span>
                                </div>
                            )}
                        </>
                    </AlertModal>
                </div>
            </PerfectScrollbar>
        </div>
    );
}

export default PainQuestionForm;